import { addDays, startOfDay, subDays } from "date-fns";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { ExpectedCloseV2CompanyForecast } from "../expected-close-v2/localExpectedCloseForecast";
import { FORECAST_COMPARISON_DAYS } from "./copy";

export type ForecastRunHistory = {
  runId: string;
  asOf: string;
  modelVersion: string;
  artifact: ExpectedCloseV2CompanyForecast;
  uncertainBuyerOutcomeIds: number[];
};

export function forecastComparisonDay(asOf: string) {
  const reference = new Date(asOf);
  if (!Number.isFinite(reference.getTime())) throw new Error("The forecast assessment date is unavailable.");
  const start = startOfDay(subDays(reference, FORECAST_COMPARISON_DAYS));
  return { start, end: addDays(start, 1) };
}

/** Reads existing company-authorized run history. Never scores, activates or writes. */
export async function loadForecastRunHistory(db: SupabaseClient, companyId: number, asOf: string, modelVersion: string): Promise<ForecastRunHistory | null> {
  const day = forecastComparisonDay(asOf);
  const runResult = await db.from("expected_close_v2_runs")
    .select("id,company_id,as_of,model_version,horizon_weeks,status,open_deals,supported_deals,forecasted_deals")
    .eq("company_id", companyId).eq("status", "completed").eq("model_version", modelVersion)
    .gte("as_of", day.start.toISOString()).lt("as_of", day.end.toISOString())
    .order("as_of", { ascending: false }).limit(1).maybeSingle();
  if (runResult.error) throw new Error("The earlier forecast run could not be read.");
  const run = runResult.data;
  if (!run) return null;
  if (Number(run.company_id) !== companyId || run.model_version !== modelVersion || run.status !== "completed"
    || run.horizon_weeks !== 53 || !(new Date(run.as_of) >= day.start && new Date(run.as_of) < day.end)) {
    throw new Error("A completed, comparable forecast run is required.");
  }
  const dealForecasts: ExpectedCloseV2CompanyForecast["dealForecasts"] = [];
  for (let page = 0; page < 50; page++) {
    const result = await db.from("expected_close_v2_deal_forecasts").select("deal_id,monthly_won_probabilities")
      .eq("company_id", companyId).eq("run_id", run.id).order("deal_id")
      .range(page * 1000, (page + 1) * 1000 - 1).abortSignal(AbortSignal.timeout(30_000));
    if (result.error) throw new Error("The earlier Deal forecasts could not be read.");
    for (const row of result.data) {
      const monthly: unknown = row.monthly_won_probabilities;
      if (!Number.isSafeInteger(Number(row.deal_id)) || !Array.isArray(monthly)
        || monthly.some(item => !Array.isArray(item) || item.length !== 2 || !/^\d{4}-(0[1-9]|1[0-2])$/.test(item[0])
          || typeof item[1] !== "number" || !Number.isFinite(item[1]) || item[1] < 0 || item[1] > 1)
        || new Set(monthly.map(item => item[0])).size !== monthly.length
        || monthly.reduce((sum, item) => sum + item[1], 0) > 1.000001) throw new Error("The earlier Deal forecast format is unavailable.");
      dealForecasts.push({ dealId: Number(row.deal_id), wonProbabilityByMonth: monthly as Array<[string, number]> });
    }
    if (result.data.length < 1000) break;
    if (page === 49) throw new Error("The earlier forecast exceeds the supported transport size.");
  }
  if (dealForecasts.length !== Number(run.forecasted_deals)
    || new Set(dealForecasts.map(deal => deal.dealId)).size !== dealForecasts.length) throw new Error("The earlier forecast is incomplete.");

  // Reuse the existing terminal/reopened signal contract. A record rewritten
  // after the comparison date cannot establish its old state; omit that Deal's change.
  const latest = new Map<number, { state: string; confidence: number; detectedAt: string; uncertain: boolean }>();
  for (let page = 0; page < 50; page++) {
    const result = await db.from("revenue_intelligence_signals")
      .select("deal_id,detected_at,created_at,updated_at,priority_dimensions,confidence")
      .eq("company_id", companyId).in("priority_dimensions->>buyer_outcome_state", ["terminal", "reopened"])
      .lte("detected_at", run.as_of).lte("created_at", run.as_of)
      .order("detected_at", { ascending: false }).order("created_at", { ascending: false }).order("id")
      .range(page * 1000, (page + 1) * 1000 - 1).abortSignal(AbortSignal.timeout(30_000));
    if (result.error) throw new Error("The earlier buyer outcomes could not be verified.");
    for (const row of result.data) {
      if (row.deal_id === null || latest.has(Number(row.deal_id))) continue;
      latest.set(Number(row.deal_id), { state: row.priority_dimensions.buyer_outcome_state,
        confidence: Number(row.confidence ?? 0), detectedAt: row.detected_at,
        uncertain: new Date(row.updated_at) > new Date(run.as_of) });
    }
    if (result.data.length < 1000) break;
    if (page === 49) throw new Error("The buyer-outcome history exceeds the supported transport size.");
  }
  return { runId: run.id, asOf: run.as_of, modelVersion, uncertainBuyerOutcomeIds: [...latest].filter(([, row]) => row.uncertain).map(([id]) => id),
    artifact: { companyId, asOf: run.as_of, modelVersion, openDeals: Number(run.open_deals), supportedDeals: Number(run.supported_deals),
      matchedSupportedDeals: dealForecasts.length, dealForecasts,
      terminalBuyerOutcomeDeals: [...latest].filter(([, row]) => row.state === "terminal" && !row.uncertain)
        .map(([dealId, row]) => ({ dealId, confidence: row.confidence, detectedAt: row.detectedAt, evidence: "", sourceMessageId: null })) } };
}
